import { useQuery } from '@tanstack/react-query';
import { PiPaperPlaneTiltBold } from 'react-icons/pi';
import { Button, LoadingState } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';

type EmailDraft = {
    to: string | null;
    cc: string[];
    bcc: string[];
    reply_to: string | null;
    subject: string;
    body: string;
    languages: string[];
    mailto: string;
};

type EmailDraftResponse = {
    draft: EmailDraft | null;
    html: string | null;
    html_error: string | null;
    pdfs: { lang: string; url: string }[];
    can_send: boolean;
};

type Props = {
    open: boolean;
    shipmentId: string;
    isResend: boolean;
    sending: boolean;
    onClose: () => void;
    onSend: () => void;
};

export default function PoEmailPreviewModal({
    open,
    shipmentId,
    isResend,
    sending,
    onClose,
    onSend,
}: Props) {
    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['shipment-po-email-draft', shipmentId],
        queryFn: async () =>
            (await api.get<EmailDraftResponse>(`/shipments/${shipmentId}/purchase-order/editor/email-draft`)).data,
        enabled: open,
        staleTime: 0,
    });

    const draft = data?.draft ?? null;
    const canSend = Boolean(data?.can_send);

    return (
        <V2Modal
            open={open}
            onClose={sending ? () => undefined : onClose}
            title="Pré-visualização do e-mail"
            description="Confira destinatários e conteúdo antes de enviar a Purchase Order ao fornecedor."
            size="5xl"
            footer={
                <>
                    <Button variant="outline" onClick={onClose} disabled={sending}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={onSend}
                        isLoading={sending}
                        disabled={!canSend || isLoading || !draft?.to}
                        className="bg-emerald-600 hover:bg-emerald-700"
                    >
                        <PiPaperPlaneTiltBold className="me-1.5 h-4 w-4" />
                        {isResend ? 'Reenviar e-mail' : 'Enviar e-mail'}
                    </Button>
                </>
            }
        >
            {isLoading ? (
                <LoadingState variant="inline" />
            ) : isError ? (
                <p className="text-sm text-red-600">
                    {(error as { response?: { data?: { message?: string } } })?.response?.data?.message
                        ?? 'Não foi possível carregar o preview do e-mail.'}
                </p>
            ) : !draft ? (
                <p className="text-sm text-gray-500">
                    Nenhum template de e-mail configurado para este fornecedor. Cadastre o e-mail e o template em
                    Configurações → Fornecedores.
                </p>
            ) : (
                <div className="space-y-4 text-sm">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/40">
                        <dl className="grid grid-cols-[90px_1fr] gap-y-1.5 text-[13px]">
                            <dt className="text-gray-500">Para:</dt>
                            <dd className="font-medium text-gray-900 dark:text-gray-100">{draft.to || '—'}</dd>

                            {draft.cc?.length > 0 ? (
                                <>
                                    <dt className="text-gray-500">CC:</dt>
                                    <dd>{draft.cc.join(', ')}</dd>
                                </>
                            ) : null}

                            {draft.bcc?.length > 0 ? (
                                <>
                                    <dt className="text-gray-500">CCO:</dt>
                                    <dd>{draft.bcc.join(', ')}</dd>
                                </>
                            ) : null}

                            {draft.reply_to ? (
                                <>
                                    <dt className="text-gray-500">Reply-To:</dt>
                                    <dd>{draft.reply_to}</dd>
                                </>
                            ) : null}

                            <dt className="text-gray-500">Assunto:</dt>
                            <dd className="font-semibold text-gray-900 dark:text-gray-100">{draft.subject}</dd>

                            <dt className="text-gray-500">PDF:</dt>
                            <dd className="flex flex-wrap items-center gap-1.5">
                                {(data?.pdfs?.length ? data.pdfs : draft.languages.map((lang) => ({ lang, url: '' }))).map(
                                    (pdf) =>
                                        pdf.url ? (
                                            <a
                                                key={pdf.lang}
                                                href={pdf.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100"
                                            >
                                                {pdf.lang}
                                            </a>
                                        ) : (
                                            <span
                                                key={pdf.lang}
                                                className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-800 dark:bg-gray-700 dark:text-gray-100"
                                            >
                                                {pdf.lang}
                                            </span>
                                        ),
                                )}
                                {!(data?.pdfs?.length) && !draft.languages?.length ? (
                                    <span className="text-gray-500">—</span>
                                ) : null}
                            </dd>
                        </dl>
                    </div>

                    {!canSend ? (
                        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                            Feche a PO e gere o PDF antes de enviar. O preview acima usa o template do fornecedor.
                        </p>
                    ) : null}

                    {data?.html_error ? (
                        <pre className="overflow-x-auto rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                            {data.html_error}
                        </pre>
                    ) : data?.html ? (
                        <div>
                            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Pré-visualização
                            </div>
                            <iframe
                                title="Preview do e-mail da PO"
                                srcDoc={data.html}
                                className="h-[420px] w-full rounded-lg border border-gray-200 bg-white dark:border-gray-700"
                                sandbox="allow-same-origin"
                            />
                        </div>
                    ) : (
                        <div>
                            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Corpo (texto)
                            </div>
                            <div className="whitespace-pre-line rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/40">
                                {draft.body}
                            </div>
                        </div>
                    )}

                    {draft.mailto ? (
                        <a
                            href={draft.mailto}
                            className="inline-flex text-xs font-medium text-primary hover:underline"
                        >
                            Abrir no cliente de e-mail
                        </a>
                    ) : null}
                </div>
            )}
        </V2Modal>
    );
}
